import { queryOptions } from '@tanstack/react-query'
import { apiExchange, apiGet } from './client'
import { saveBytes } from './inventoree'

/**
 * Raportul Easeedesk din rapoartele de firma -
 * `settings/Company_reports::easeedesk_report`.
 *
 * Perechile vechi:
 *
 *   easeedesk_report              -> GET /settings/company-reports/easeedesk
 *   getEaseedeskCriticalIncidents -> GET .../easeedesk/critical-incidents
 *   getEaseedeskIncidentsStatus   -> GET .../easeedesk/incidents-status
 *   getEaseedeskOpenIncidents     -> GET .../easeedesk/open-incidents
 *   getEaseedeskIncidentsPriority -> GET .../easeedesk/incidents-priority
 *   getEaseedeskResolvedTickets   -> GET .../easeedesk/resolved-tickets
 *   getEaseedeskServiceCosts      -> GET .../easeedesk/service-costs
 *   Easeedesk::getGroupsUsers     -> GET .../easeedesk/users?groups[]=
 *   generateEaseedesk*Excel (x5)  -> GET .../easeedesk/export.xlsx?report_type=N
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`.
 *
 * EXPORTUL vine ca fisier din acelasi raspuns (`attachment`), nu ca nume de
 * fisier de descarcat apoi prin `download_script_file` - usa aceea n-a mutat-o
 * nimeni, dinadins.
 */

export type GenerateTime = 'days' | 'weeks'

export interface Named {
  ID: number
  [key: string]: unknown
}

export interface ReportTicketType {
  ID: number
  type_name: string
}

export interface ReportPriority {
  ID: number
  priority_name: string
  priority_color: string
}

export interface ReportGroup {
  ID: number
  name: string
}

export interface ReportKind {
  id: number
  label: string
}

/** O luna calculata a costurilor: `202607` si "July 2026". */
export interface CostPeriod {
  ym: number
  label: string
}

export interface ReportsShell {
  title: string
  moduleUrl: string
  /** Tabelele raportului exista pe baza asta? */
  ready: boolean
  reports: ReportKind[]
  generateTimes: GenerateTime[]
  ticketTypes: ReportTicketType[]
  priorities: ReportPriority[]
  groups: ReportGroup[]
  /** Lunile deja calculate ale costurilor, cea mai noua intai. */
  costPeriods: CostPeriod[]
  /** Bifa "All companies": raportul numara peste toate firmele omului. */
  allCompanies: boolean
  /** `inventoree.service_catalog.edit`: butonul "Work out a month". */
  canBuildCosts: boolean
}

export interface ReportUser {
  ID: number
  user_name: string
}

/** Raportul 5: coloanele (camp => eticheta) si randurile, toate ca text. */
export interface ResolvedTickets {
  columns: Record<string, string>
  rows: Record<string, string | null>[]
  nagios: boolean
}

export interface ServiceCostRow {
  catalog_id: number
  service_name: string
  /** 4 = serviciu de business; altceva = IT. */
  kind: number
  company_name: string
  category_name: string
  ci_count: number
  user_count: number
  ticket_count: number
  amount: number
  currency: string
}

export interface NamedAmount {
  name: string
  amount: number
}

/** Raportul 6: o luna, randurile ei si totalurile pe moneda si pe client. */
export interface ServiceCosts {
  period_ym: number
  period_label: string
  rows: ServiceCostRow[]
  totals: { currency: NamedAmount[]; company: NamedAmount[] }
}

/** Filtrele raportului 5, cum le trimite ecranul. */
export interface ResolvedFilters {
  groups: number[]
  users: number[]
  ticketTypes: number[]
  dateFrom: string
  dateTo: string
  oncall: boolean
  nagios: boolean
}

export interface CategoryRow {
  category_name: string
  records: number[]
}

export interface CriticalIncidents {
  periods: string[]
  generate_time: GenerateTime
  priority_name: string
  rows: CategoryRow[]
  total_records: number[]
  /** SVG desenat de server (`IncidentPanels`), nu de o biblioteca din browser. */
  chart: string
}

export type StatusKey = 'resolved' | 'created' | 'still_open' | 'resolved_sla'

export interface IncidentsStatus {
  periods: string[]
  generate_time: GenerateTime
  names: Record<StatusKey, string>
  series: Record<StatusKey, number[]>
  chart: string
}

export interface PriorityRow {
  priority_name: string
  priority_color: string
  records: number[]
}

export interface IncidentsPriority {
  periods: string[]
  generate_time: GenerateTime
  rows: PriorityRow[]
  chart: string
}

export interface OpenGroupRow {
  group_name: string
  sla_1: number
  sla_2: number
  sla_3: number
  sla_4: number
}

export interface OpenIncidentsType {
  type_id: number
  type_name: string
  rows: OpenGroupRow[]
  total: { sla_1: number; sla_2: number; sla_3: number; sla_4: number }
}

export interface OpenIncidents {
  types: OpenIncidentsType[]
}

const BASE = '/settings/company-reports/easeedesk'

function withIds(params: URLSearchParams, key: string, ids: number[]): URLSearchParams {
  for (const id of ids) params.append(`${key}[]`, String(id))
  return params
}

export const easeedeskReportsShellQuery = queryOptions({
  queryKey: ['settings', 'company-reports', 'easeedesk'],
  queryFn: () => apiGet<ReportsShell>(BASE),
})

export function criticalIncidentsQuery(generateTime: GenerateTime, priority: number, ticketTypes: number[]) {
  const p = withIds(new URLSearchParams({ generate_time: generateTime, priority: String(priority) }), 'ticket_types', ticketTypes)

  return queryOptions({
    queryKey: ['settings', 'company-reports', 'easeedesk', 'critical', generateTime, priority, ticketTypes],
    queryFn: () => apiGet<CriticalIncidents>(`${BASE}/critical-incidents?${p.toString()}`),
    /* Fara prioritate aleasa raportul n-are ce numara - ca in ecranul vechi,
       unde casuta de prioritate era obligatorie pentru felul asta de raport. */
    enabled: priority > 0,
  })
}

export function incidentsStatusQuery(generateTime: GenerateTime, ticketTypes: number[]) {
  const p = withIds(new URLSearchParams({ generate_time: generateTime }), 'ticket_types', ticketTypes)

  return queryOptions({
    queryKey: ['settings', 'company-reports', 'easeedesk', 'status', generateTime, ticketTypes],
    queryFn: () => apiGet<IncidentsStatus>(`${BASE}/incidents-status?${p.toString()}`),
  })
}

export function incidentsPriorityQuery(generateTime: GenerateTime, ticketTypes: number[]) {
  const p = withIds(new URLSearchParams({ generate_time: generateTime }), 'ticket_types', ticketTypes)

  return queryOptions({
    queryKey: ['settings', 'company-reports', 'easeedesk', 'priority', generateTime, ticketTypes],
    queryFn: () => apiGet<IncidentsPriority>(`${BASE}/incidents-priority?${p.toString()}`),
  })
}

export function openIncidentsQuery(ticketTypes: number[], groups: number[]) {
  const p = withIds(withIds(new URLSearchParams(), 'ticket_types', ticketTypes), 'groups', groups)

  return queryOptions({
    queryKey: ['settings', 'company-reports', 'easeedesk', 'open', ticketTypes, groups],
    queryFn: () => apiGet<OpenIncidents>(`${BASE}/open-incidents?${p.toString()}`),
    /* Raportul e pe tip de tichet: fara niciunul ales n-are ce desena. */
    enabled: ticketTypes.length > 0,
  })
}

export function groupUsersQuery(groups: number[]) {
  const p = withIds(new URLSearchParams(), 'groups', groups)

  return queryOptions({
    queryKey: ['settings', 'company-reports', 'easeedesk', 'users', groups],
    queryFn: () => apiGet<{ users: ReportUser[] }>(`${BASE}/users?${p.toString()}`),
    enabled: groups.length > 0,
  })
}

function resolvedParams(f: ResolvedFilters): URLSearchParams {
  const p = new URLSearchParams({ date_from: f.dateFrom, date_to: f.dateTo })
  withIds(p, 'groups', f.groups)
  withIds(p, 'users', f.users)
  withIds(p, 'ticket_types', f.ticketTypes)
  if (f.oncall) p.set('oncall', '1')
  if (f.nagios) p.set('nagios', '1')
  return p
}

export function resolvedTicketsQuery(f: ResolvedFilters) {
  return queryOptions({
    queryKey: ['settings', 'company-reports', 'easeedesk', 'resolved', f],
    queryFn: () => apiGet<ResolvedTickets>(`${BASE}/resolved-tickets?${resolvedParams(f).toString()}`),
    /* Fara fereastra raportul ar numara toate tichetele de cand e baza. */
    enabled: f.dateFrom !== '' && f.dateTo !== '',
  })
}

export function serviceCostsQuery(periodYm: number) {
  return queryOptions({
    queryKey: ['settings', 'company-reports', 'easeedesk', 'costs', periodYm],
    queryFn: () => apiGet<ServiceCosts>(`${BASE}/service-costs?period_ym=${periodYm}`),
  })
}

/**
 * Butonul "Export": aceleasi filtre ca raportul de pe ecran, deci fisierul
 * nu poate spune altceva decat ce s-a vazut. `params` sunt cele ale
 * raportului ales; numarul lui spune serverului ce foaie sa scrie.
 */
export async function downloadReportXlsx(reportType: number, params: URLSearchParams): Promise<void> {
  params.set('report_type', String(reportType))
  const out = await apiExchange(`${BASE}/export.xlsx?${params.toString()}`, { method: 'GET' })
  saveBytes(out.bytes, out.type, out.filename, 'report.xlsx')
}

/** Parametrii de export ai fiecarui raport - aceiasi pe care ii primeste si raportul. */
export function exportParams(
  reportType: number,
  filters: { generateTime: GenerateTime; priority: number; ticketTypes: number[]; groups: number[]; resolved: ResolvedFilters },
): URLSearchParams {
  if (reportType === 5) return resolvedParams(filters.resolved)
  if (reportType === 3) return withIds(withIds(new URLSearchParams(), 'ticket_types', filters.ticketTypes), 'groups', filters.groups)
  const p = withIds(new URLSearchParams({ generate_time: filters.generateTime }), 'ticket_types', filters.ticketTypes)
  if (reportType === 1) p.set('priority', String(filters.priority))
  return p
}
